import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categoryList, getCategoryPath, sortByHeat } from "@/utils/content";

const navLinks = [
  { to: "/", label: "首页", end: true },
  { to: "/events", label: "瓜田总览", end: false },
  { to: "/about", label: "关于我们", end: false },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const hotList = sortByHeat().slice(0, 6);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/events?q=${encodeURIComponent(value)}` : "/events");
    setMenuOpen(false);
  };

  const linkClass = (isActive: boolean) =>
    [
      "px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap",
      isActive
        ? "text-primary-700 bg-primary-100 font-medium"
        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100",
    ].join(" ");

  return (
    <header className="sticky top-0 z-40 w-full">
      <div className="w-full bg-primary-600 text-background-50">
        <div className="mx-auto flex max-w-content items-center gap-3 px-4 py-2 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium tracking-wide">
            <i className="ri-flashlight-fill text-accent-300"></i>
            实时热榜
          </span>
          <div className="relative w-full overflow-hidden">
            <div className="flex w-max animate-ticker gap-8 pr-8">
              {[...hotList, ...hotList].map((item, index) => (
                <Link
                  key={`${item.id}-${index}`}
                  to={`/event/${item.slug}`}
                  className="whitespace-nowrap text-xs text-background-50/90 transition-colors hover:text-accent-200"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label="热搜吃瓜首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-lg"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-lg font-bold text-foreground-950">
                热搜吃瓜
              </span>
              <span className="hidden text-[11px] text-foreground-500 sm:block">
                事件梳理 · 时间线复盘
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) => linkClass(isActive)}
              >
                {link.label}
              </NavLink>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                aria-expanded={categoryOpen}
              >
                主题分类
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    categoryOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full w-72 pt-2">
                  <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                    {categoryList.map((category) => (
                      <Link
                        key={category.slug}
                        to={getCategoryPath(category.slug)}
                        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                        onClick={() => setCategoryOpen(false)}
                      >
                        <i className={`${category.icon} text-secondary-600`}></i>
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <form onSubmit={handleSearch} className="relative" role="search">
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件关键词"
                className="w-52 rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                aria-label="搜索事件关键词"
              />
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            </form>
            <Link
              to="/#subscribe"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              订阅瓜报
            </Link>
          </div>

          <button
            type="button"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
            <form onSubmit={handleSearch} className="relative mb-3" role="search">
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                aria-label="搜索事件关键词"
              />
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            </form>
            <nav className="flex flex-col gap-1" aria-label="移动端导航">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm ${
                      isActive
                        ? "bg-primary-100 font-medium text-primary-700"
                        : "text-foreground-700"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="mb-2 text-xs text-foreground-500">主题分类</p>
              <div className="grid grid-cols-2 gap-1">
                {categoryList.map((category) => (
                  <Link
                    key={category.slug}
                    to={getCategoryPath(category.slug)}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 hover:bg-secondary-100"
                  >
                    <i className={`${category.icon} text-secondary-600`}></i>
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/#subscribe"
              onClick={() => setMenuOpen(false)}
              className="mt-4 block rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50"
            >
              订阅瓜报
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}